import { useEffect, useRef, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';

const API = 'https://guardiancloudcare.com/backend/api';

interface CaseItem {
  case_number: string;
  uid?: string;
  case_uid?: string;
  report_uid?: string;
  victim_uid?: string;
  victim_name?: string;
  child_first_name?: string;
  child_middle_name?: string;
  child_last_name?: string;
  incident_type?: string;
  case_status?: string;
  assigned_social_worker_uid?: string;
  social_worker_name?: string;
}

interface Message {
  id: number;
  case_uid: string;
  report_uid: string;
  sender_uid: string;
  sender_role: string;
  receiver_uid: string;
  receiver_role: string;
  message: string;
  is_read: number;
  created_at: string;
  updated_at?: string;
}

export default function VMessagesPage() {
  const { user } = useAuth();

  const [cases, setCases] = useState<CaseItem[]>([]);
  const [selectedCase, setSelectedCase] = useState<CaseItem | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageText, setMessageText] = useState('');

  const [loadingCases, setLoadingCases] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);

  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const getCaseUid = (item: CaseItem) => {
    return item.case_uid || item.report_uid || item.uid || '';
  };

  const getVictimUid = (item: CaseItem) => {
    return item.victim_uid || user?.uid || '';
  };

  const getVictimName = (item: CaseItem) => {
    if (item.victim_name?.trim()) {
      return item.victim_name.trim();
    }

    const name = [
      item.child_first_name,
      item.child_middle_name,
      item.child_last_name,
    ]
      .filter(Boolean)
      .join(' ')
      .trim();

    return name || 'My Case';
  };

  const getSocialWorkerName = (item: CaseItem) => {
    return item.social_worker_name?.trim() || 'Assigned Social Worker';
  };

  const fetchCases = async () => {
    if (!user?.uid) {
      setCases([]);
      setLoadingCases(false);
      setError('User session not found.');
      return;
    }

    try {
      setLoadingCases(true);
      setError('');

      const response = await fetch(
        `${API}/get_victim_cases_message.php?uid=${encodeURIComponent(
          user.uid
        )}`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.error ||
            data?.message ||
            `Failed to load cases. HTTP ${response.status}`
        );
      }

      const list: CaseItem[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.cases)
        ? data.cases
        : [];

      const validCases = list.filter(
        (item) =>
          Boolean(getCaseUid(item)) &&
          Boolean(item.victim_uid || user.uid)
      );

      setCases(validCases);

      if (validCases.length > 0) {
        setSelectedCase((current) => {
          if (!current) {
            return validCases[0];
          }

          return (
            validCases.find(
              (item) =>
                item.case_number === current.case_number
            ) || validCases[0]
          );
        });
      } else {
        setSelectedCase(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Failed to load victim cases:', err);

      setCases([]);
      setSelectedCase(null);
      setMessages([]);

      setError(
        err instanceof Error
          ? err.message
          : 'Unable to load your cases.'
      );
    } finally {
      setLoadingCases(false);
    }
  };

  const fetchMessages = async (caseItem: CaseItem) => {
    if (!user?.uid) {
      setMessages([]);
      setError('User session not found.');
      return;
    }

    const caseUid = getCaseUid(caseItem);
    const victimUid = getVictimUid(caseItem);

    if (!caseUid || !victimUid) {
      setMessages([]);
      setError(
        'This case does not have the required messaging information.'
      );
      return;
    }

    try {
      setLoadingMessages(true);
      setError('');

      const params = new URLSearchParams({
        case_uid: caseUid,
        uid: user.uid,
        role: 'victim',
      });

      const response = await fetch(
        `${API}/get_case_messages.php?${params.toString()}`
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data?.error ||
            data?.message ||
            'Unable to load messages.'
        );
      }

      setMessages(
        Array.isArray(data.messages)
          ? data.messages
          : []
      );
    } catch (err) {
      console.error('Failed to load messages:', err);

      setMessages([]);

      setError(
        err instanceof Error
          ? err.message
          : 'Unable to load messages.'
      );
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    fetchCases();
  }, [user?.uid]);

  useEffect(() => {
    if (!selectedCase) {
      return;
    }

    fetchMessages(selectedCase);

    const interval = setInterval(() => {
      fetchMessages(selectedCase);
    }, 5000);

    return () => clearInterval(interval);
  }, [
    selectedCase?.case_number,
    selectedCase?.uid,
    selectedCase?.case_uid,
    selectedCase?.report_uid,
    user?.uid,
  ]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
    });
  }, [messages]);

  const sendMessage = async () => {
    if (!user?.uid) {
      setError('User session not found.');
      return;
    }

    if (!selectedCase) {
      setError('Please select a case first.');
      return;
    }

    if (!messageText.trim()) {
      return;
    }

    const caseUid = getCaseUid(selectedCase);
    const victimUid = getVictimUid(selectedCase);

    if (!caseUid || !victimUid) {
      setError(
        'This case does not have the required messaging information.'
      );
      return;
    }

    const socialWorkerUid =
      selectedCase.assigned_social_worker_uid || '';

    if (!socialWorkerUid) {
      setError(
        'No Social Worker is currently assigned to this case.'
      );
      return;
    }

    try {
      setSending(true);
      setError('');

      const response = await fetch(
        `${API}/send_case_message.php`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            case_uid: caseUid,
            report_uid: caseUid,
            sender_uid: user.uid,
            sender_role: 'victim',
            receiver_uid: socialWorkerUid,
            receiver_role: 'social_worker',
            message: messageText.trim(),
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data?.error ||
            data?.message ||
            'Unable to send message.'
        );
      }

      setMessageText('');

      await fetchMessages(selectedCase);
    } catch (err) {
      console.error('Send message error:', err);

      setError(
        err instanceof Error
          ? err.message
          : 'Unable to send message.'
      );
    } finally {
      setSending(false);
    }
  };

  const filteredCases = cases.filter((item) => {
    const keyword = search.toLowerCase().trim();

    if (!keyword) {
      return true;
    }

    const text = [
      getVictimName(item),
      getSocialWorkerName(item),
      item.case_number,
      item.incident_type,
      item.case_status,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return text.includes(keyword);
  });

  return (
    <AppLayout title="Messages">
      <div className="min-h-full bg-gray-50 p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center">
                <i className="ri-message-3-line text-2xl" />
              </div>

              <div>
                <h1 className="text-3xl font-bold text-[#1f2a5a]">
                  Messages
                </h1>

                <p className="text-gray-500 mt-1">
                  Communicate directly with your assigned Social Worker.
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center gap-2">
              <i className="ri-error-warning-line" />

              <span>{error}</span>

              <button
                onClick={() => setError('')}
                className="ml-auto"
                type="button"
              >
                <i className="ri-close-line" />
              </button>
            </div>
          )}

          <div className="bg-white border border-gray-100 rounded-3xl shadow-sm overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-[390px_1fr] min-h-[650px]">
              <div className="border-r border-gray-100 flex flex-col">
                <div className="p-5 border-b border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="font-bold text-gray-900">
                        My Cases
                      </h2>

                      <p className="text-xs text-gray-500 mt-1">
                        Cases connected to your account
                      </p>
                    </div>

                    <button
                      onClick={fetchCases}
                      type="button"
                      className="w-9 h-9 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50"
                      title="Refresh cases"
                    >
                      <i className="ri-refresh-line" />
                    </button>
                  </div>

                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />

                    <input
                      type="text"
                      value={search}
                      onChange={(e) =>
                        setSearch(e.target.value)
                      }
                      placeholder="Search cases..."
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {loadingCases ? (
                    <div className="p-8 text-center text-gray-400">
                      <i className="ri-loader-4-line animate-spin text-2xl" />

                      <p className="text-sm mt-2">
                        Loading your cases...
                      </p>
                    </div>
                  ) : filteredCases.length === 0 ? (
                    <div className="p-8 text-center">
                      <div className="w-14 h-14 mx-auto rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400">
                        <i className="ri-folder-open-line text-2xl" />
                      </div>

                      <p className="font-semibold text-gray-700 mt-3">
                        No cases found
                      </p>

                      <p className="text-xs text-gray-400 mt-1">
                        Your registered cases will appear here.
                      </p>
                    </div>
                  ) : (
                    <div className="p-2">
                      {filteredCases.map((item) => {
                        const active =
                          selectedCase?.case_number ===
                          item.case_number;

                        return (
                          <button
                            key={`${item.case_number}-${getCaseUid(
                              item
                            )}`}
                            onClick={() => {
                              setSelectedCase(item);
                              setError('');
                            }}
                            type="button"
                            className={`w-full text-left p-4 rounded-2xl transition mb-1 ${
                              active
                                ? 'bg-teal-50 border border-teal-200'
                                : 'hover:bg-gray-50 border border-transparent'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                  active
                                    ? 'bg-teal-500 text-white'
                                    : 'bg-gray-100 text-gray-500'
                                }`}
                              >
                                <i className="ri-file-user-line text-lg" />
                              </div>

                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-gray-900 truncate">
                                  {getSocialWorkerName(item)}
                                </p>

                                <p className="text-xs font-medium text-teal-600">
                                    Assigned Social Worker
                                </p>
                              </div>

                              <i
                                className={`ri-arrow-right-s-line mt-1 ${
                                  active
                                    ? 'text-teal-500'
                                    : 'text-gray-300'
                                }`}
                              />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col min-w-0">
                {!selectedCase ? (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center max-w-md">
                      <div className="w-20 h-20 mx-auto rounded-3xl bg-teal-50 text-teal-500 flex items-center justify-center">
                        <i className="ri-chat-3-line text-4xl" />
                      </div>

                      <h2 className="text-xl font-bold text-gray-900 mt-5">
                        No Case Selected
                      </h2>

                      <p className="text-gray-500 text-sm mt-2">
                        Select one of your cases to communicate
                        with your assigned Social Worker.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center">
                        <i className="ri-user-heart-line text-xl" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-teal-600">
                          Assigned Social Worker
                        </p>

                        <h2 className="font-bold text-gray-900 truncate">
                          {getSocialWorkerName(selectedCase)}
                        </h2>

                        <div className="flex flex-wrap items-center gap-2 mt-1">

                          {selectedCase.incident_type && (
                            <>
                              <span className="w-1 h-1 rounded-full bg-gray-300" />

                              <span className="text-xs text-gray-500">
                                {selectedCase.incident_type}
                              </span>
                            </>
                          )}

                          {selectedCase.case_status && (
                            <>
                              <span className="w-1 h-1 rounded-full bg-gray-300" />

                              <span className="text-xs text-gray-500">
                                {selectedCase.case_status}
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      <button
                        onClick={() =>
                          selectedCase &&
                          fetchMessages(selectedCase)
                        }
                        type="button"
                        className="w-10 h-10 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50"
                        title="Refresh messages"
                      >
                        <i className="ri-refresh-line" />
                      </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-5 bg-gray-50">
                      {loadingMessages ? (
                        <div className="h-full flex items-center justify-center">
                          <div className="text-center text-gray-400">
                            <i className="ri-loader-4-line animate-spin text-2xl" />

                            <p className="text-sm mt-2">
                              Loading conversation...
                            </p>
                          </div>
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="h-full flex items-center justify-center">
                          <div className="text-center max-w-sm">
                            <div className="w-16 h-16 mx-auto rounded-2xl bg-white border border-gray-100 flex items-center justify-center text-gray-400">
                              <i className="ri-chat-1-line text-2xl" />
                            </div>

                            <h3 className="font-semibold text-gray-700 mt-4">
                              No messages yet
                            </h3>

                            <p className="text-xs text-gray-400 mt-1">
                              Start a conversation with your
                              assigned Social Worker.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {messages.map((message) => {
                            const mine =
                              message.sender_uid ===
                                user?.uid ||
                              message.sender_role ===
                                'victim';

                            return (
                              <div
                                key={message.id}
                                className={`flex ${
                                  mine
                                    ? 'justify-end'
                                    : 'justify-start'
                                }`}
                              >
                                <div
                                  className={`max-w-[80%] ${
                                    mine
                                      ? 'items-end'
                                      : 'items-start'
                                  } flex flex-col`}
                                >
                                  <div
                                    className={`px-4 py-3 rounded-2xl text-sm ${
                                      mine
                                        ? 'bg-teal-500 text-white rounded-br-md'
                                        : 'bg-white border border-gray-100 text-gray-800 rounded-bl-md shadow-sm'
                                    }`}
                                  >
                                    {message.message}
                                  </div>

                                  <span className="text-[10px] text-gray-400 mt-1 px-1">
                                    {new Date(
                                      message.created_at
                                    ).toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            );
                          })}

                          <div ref={messagesEndRef} />
                        </div>
                      )}
                    </div>

                    <div className="p-4 border-t border-gray-100 bg-white">
                      <div className="flex items-end gap-3">
                        <textarea
                          value={messageText}
                          onChange={(e) =>
                            setMessageText(e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (
                              e.key === 'Enter' &&
                              !e.shiftKey
                            ) {
                              e.preventDefault();
                              sendMessage();
                            }
                          }}
                          rows={2}
                          placeholder="Message your Social Worker..."
                          className="flex-1 resize-none rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                        />

                        <button
                          onClick={sendMessage}
                          disabled={
                            sending ||
                            !messageText.trim()
                          }
                          type="button"
                          className="w-12 h-12 rounded-2xl bg-teal-500 text-white flex items-center justify-center hover:bg-teal-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
                          title="Send message"
                        >
                          {sending ? (
                            <i className="ri-loader-4-line animate-spin" />
                          ) : (
                            <i className="ri-send-plane-fill" />
                          )}
                        </button>
                      </div>

                      <p className="text-[10px] text-gray-400 mt-2">
                        Press Enter to send. Use Shift + Enter
                        for a new line.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}